import { FilterItemType } from 'src/shared/components/Filters/types';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionButtonValues, type FlexflowEmployeeStatuses } from '../../types';
import { useUpdateFlexflowEmployeesBulkMutation } from 'src/shared/api/flexflow';
import { type FlexflowEmployeeDataToDisplay } from '../types';
import { useFormState, useForm } from 'react-final-form';
import { type ResponseWithPaginationMeta } from 'src/shared/api/types';
import { get } from 'lodash';
import { useDispatch } from 'react-redux';
import { addToast } from 'src/old/actions';
import { toastTypes } from 'src/shared/components/Toast/ToastOptions';
import i18n from 'src/i18n';
import { type Employee } from 'src/shared/types/employee';
import { FlexflowContractsTab } from '../Contracts';
import { FlexflowEmployeeStatusesBeforeActivation } from '../ContractStatusBeforeInvitation';
import { useGetUserHoldingQuery } from 'src/shared/api/holding';
import { checkIfFrWalletWasEverEnabled, checkIfSbWasEverEnabled } from 'src/shared/utils/flexflow';
import { showBulkActionConfirmationModal } from './utils';

export enum FlexflowCardDisplayedStatus {
  UNACTIVATED = 'unactivated',
  ACTIVE = 'active',
  FROZEN = 'frozen',
  SUSPENDED = 'suspended'
}

export type FlexflowContractsListFilter = {
  searchTerm?: string;
  filters?: {
    status?: FlexflowEmployeeStatuses[] | FlexflowEmployeeStatusesBeforeActivation;
    cardStatus?: FlexflowCardDisplayedStatus[];
    frWalletOn?: 'true' | 'false';
    sbAssigned?: 'true' | 'false';
  } & (
    | { status: FlexflowEmployeeStatusesBeforeActivation | undefined }
    | { cardStatus: FlexflowCardDisplayedStatus[] | undefined }
  );
} | null;

export const initialValues: FlexflowContractsListFilter = {
  searchTerm: '',
  filters: {
    status: undefined
  }
};

const getFilterScheme = (
  tab: FlexflowContractsTab,
  { showFrWalletFilter, showSocialBenefitFilter }: { showFrWalletFilter: boolean; showSocialBenefitFilter: boolean }
) => {
  const frWalletFilter = {
    type: FilterItemType.radio,
    label: i18n.t('table.acumulado on', { ns: 'flexflow' }),
    name: 'frWalletOn',
    options: [
      { label: i18n.t('yes', { ns: 'common' }), value: 'true' },
      { label: i18n.t('no', { ns: 'common' }), value: 'false' }
    ]
  };

  const socialBenefitFilter = {
    type: FilterItemType.radio,
    label: i18n.t('table.social benefit assigned', { ns: 'flexflow' }),
    name: 'sbAssigned',
    options: [
      { label: i18n.t('yes', { ns: 'common' }), value: 'true' },
      { label: i18n.t('no', { ns: 'common' }), value: 'false' }
    ]
  };

  if (tab === FlexflowContractsTab.BEFORE_ACTIVATION) {
    const excludedStatuses = [
      FlexflowEmployeeStatusesBeforeActivation.ACCEPTED,
      FlexflowEmployeeStatusesBeforeActivation.PENGING_AUTH,
      FlexflowEmployeeStatusesBeforeActivation.ACCOUNT_BINDED
    ];

    return [
      {
        type: FilterItemType.radio,
        label: i18n.t('status.title', { ns: 'filters' }),
        name: 'status',
        options: Object.values(FlexflowEmployeeStatusesBeforeActivation)
          .filter(status => !excludedStatuses.includes(status))
          .map(contractStatusValue => ({
            label: i18n.t(`employee status.${contractStatusValue}`, { ns: 'flexflow' }),
            value: contractStatusValue
          }))
      },
      ...(showFrWalletFilter ? [frWalletFilter] : []),
      ...(showSocialBenefitFilter ? [socialBenefitFilter] : [])
    ];
  }

  return [
    {
      type: FilterItemType.radio,
      label: i18n.t('table.flexflow status', { ns: 'flexflow' }),
      name: 'cardStatus',
      options: Object.values(FlexflowCardDisplayedStatus).map(cardStatusValue => {
        const label =
          cardStatusValue === FlexflowCardDisplayedStatus.SUSPENDED ? `${cardStatusValue}New` : cardStatusValue;
        return {
          label: i18n.t(`card status label.${label}`, { ns: 'flexflow' }),
          value: cardStatusValue
        };
      })
    },
    ...(showFrWalletFilter ? [frWalletFilter] : []),
    ...(showSocialBenefitFilter ? [socialBenefitFilter] : [])
  ];
};

export const useFilters = (tab: FlexflowContractsTab) => {
  const [filterValues, setFilterValues] = useState<FlexflowContractsListFilter>(initialValues);
  const { data: holdingData } = useGetUserHoldingQuery();
  const flexflowSettings = holdingData?.settings?.flexflow;
  const showFrWalletFilter = checkIfFrWalletWasEverEnabled(flexflowSettings);
  const showSocialBenefitFilter = checkIfSbWasEverEnabled(flexflowSettings);

  const handleFiltersChange = useCallback(
    (filters: FlexflowContractsListFilter) => {
      setFilterValues(filters);
    },
    [setFilterValues]
  );

  const filterScheme = useMemo(
    () => getFilterScheme(tab, { showFrWalletFilter, showSocialBenefitFilter }),
    [tab, showFrWalletFilter, showSocialBenefitFilter]
  );

  return { filterValues, filterScheme, setFilterValues: handleFiltersChange };
};

export const useBulkActionsOptions = () => {
  const { t } = useTranslation(['flexflow', 'contract']);
  const bulkActionsOptions = [
    {
      label: t('invite', { ns: 'flexflow' }),
      value: ActionButtonValues.INVITE
    },
    {
      label: t('disable', { ns: 'flexflow' }),
      value: ActionButtonValues.NOT_INVITED
    }
  ];
  return bulkActionsOptions;
};

export const useBulkActions = (data: ResponseWithPaginationMeta<FlexflowEmployeeDataToDisplay | Employee>) => {
  const { values } = useFormState();
  const [updateFlexflowEmployeesBulk] = useUpdateFlexflowEmployeesBulkMutation();
  const form = useForm();
  const { t } = useTranslation('common');
  const selectedContracts = useMemo(() => {
    return data.data.filter((_, i) => get(values, `table[${i}].isSelected`));
  }, [data, values]);

  const dispatch = useDispatch();
  const updateStatus = async (actionType: ActionButtonValues) => {
    try {
      await updateFlexflowEmployeesBulk({
        employeeIds: selectedContracts.map((contract: FlexflowEmployeeDataToDisplay | Employee) => {
          return Number(contract.id);
        }),
        data: {
          invitationStatus: actionType
        }
      }).unwrap();
      dispatch(addToast({ ...toastTypes.success, defaultMessage: i18n.t('flexflow.put.success', { ns: 'actions' }) }));
    } catch (error) {
      dispatch(addToast({ ...toastTypes.error, defaultMessage: i18n.t('flexflow.put.error', { ns: 'actions' }) }));
    }
  };
  const handleBulkActions = async (actionType: ActionButtonValues) => {
    if (!actionType) {
      return;
    }
    showBulkActionConfirmationModal({
      dispatch,
      t,
      selectedContracts: selectedContracts.length,
      actionType,
      onConfirm: updateStatus
    });
    form.change('bulkActions', undefined);
  };

  return { handleBulkActions, selectedContracts };
};
